import { Check, Copy } from "lucide-react";
import { type ReactNode, useState } from "react";
import { IconButton } from "../atoms/IconButton";

// STATEFUL EXCEPTION: This component holds a brief "Copied!" feedback state
// (~2s timeout) triggered by a user gesture (clipboard write). This is purely
// visual feedback — no business logic, no data fetching. All other data flows
// in via props. Justified per spec.

const COPIED_FEEDBACK_MS = 2000;

export interface CopyButtonProps {
	value: string;
	onCopied?: () => void;
	className?: string;
}

function CopyButton({
	value,
	onCopied,
	className,
}: CopyButtonProps): ReactNode {
	const [copied, setCopied] = useState(false);

	function handleCopy(): void {
		navigator.clipboard.writeText(value).then(() => {
			setCopied(true);
			onCopied?.();
			setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
		});
	}

	return (
		<IconButton
			aria-label={copied ? "Copied!" : "Copy to clipboard"}
			size="sm"
			variant="ghost"
			onClick={handleCopy}
			className={className}
		>
			{copied ? (
				<Check className="h-3 w-3 text-statusSuccess" aria-hidden="true" />
			) : (
				<Copy className="h-3 w-3" aria-hidden="true" />
			)}
		</IconButton>
	);
}
CopyButton.displayName = "CopyButton";

export default CopyButton;
export { CopyButton };
